import React from 'react';
import {
  Box,
  Container,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Card,
  CardContent,
  Chip,
  Button,
  useTheme,
  alpha,
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';

interface FunnelSlot {
  id: string;
  title: string;
  description?: string;
  tags?: string[];
  content?: React.ReactNode;
  icon?: React.ReactNode;
  color?: string;
}

interface FunnelLayoutProps {
  title?: string;
  description?: string;
  slots: FunnelSlot[];
  orientation?: 'vertical' | 'horizontal';
  showProgress?: boolean;
  activeStep?: number;
  completedSteps?: number[];
  onStepClick?: (stepIndex: number) => void;
  children?: React.ReactNode;
}

/**
 * FunnelLayout component for creating guided, step-by-step content experiences
 * Supports named slots derived from tag groups or direct definition via props
 */
export const FunnelLayout: React.FC<FunnelLayoutProps> = ({
  title,
  description,
  slots,
  orientation = 'vertical',
  showProgress = true,
  activeStep = 0,
  completedSteps = [],
  onStepClick,
  children,
}) => {
  const theme = useTheme();

  const handleStepClick = (index: number) => {
    if (onStepClick) {
      onStepClick(index);
    }
  };

  const renderVerticalFunnel = () => (
    <Box sx={{ width: '100%' }}>
      {title && (
        <Typography variant="h4" gutterBottom sx={{ mb: 1 }}>
          {title}
        </Typography>
      )}
      {description && (
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          {description}
        </Typography>
      )}

      <Stepper activeStep={activeStep} orientation="vertical">
        {slots.map((slot, index) => {
          const isCompleted = completedSteps.includes(index);
          const isActive = activeStep === index;

          return (
            <Step key={slot.id} completed={isCompleted}>
              <StepLabel
                onClick={() => handleStepClick(index)}
                sx={{
                  cursor: onStepClick ? 'pointer' : 'default',
                  '& .MuiStepLabel-label': {
                    fontSize: '1.1rem',
                    fontWeight: isActive ? 600 : 400,
                  },
                }}
                StepIconComponent={slot.icon ? () => <Box sx={{ color: slot.color || theme.palette.primary.main }}>{slot.icon}</Box> : undefined}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {slot.title}
                  {isCompleted && (
                    <CheckIcon
                      sx={{
                        fontSize: '1rem',
                        color: theme.palette.success.main,
                      }}
                    />
                  )}
                </Box>
                {slot.tags && slot.tags.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                    {slot.tags.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.75rem' }}
                      />
                    ))}
                  </Box>
                )}
              </StepLabel>
              <StepContent>
                <Card
                  sx={{
                    mb: 2,
                    borderLeft: `4px solid ${slot.color || theme.palette.primary.main}`,
                    backgroundColor: isActive
                      ? alpha(slot.color || theme.palette.primary.main, 0.05)
                      : 'background.paper',
                  }}
                >
                  <CardContent>
                    {slot.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {slot.description}
                      </Typography>
                    )}
                    {slot.content}
                  </CardContent>
                </Card>
              </StepContent>
            </Step>
          );
        })}
      </Stepper>

      {children && (
        <Box sx={{ mt: 4 }}>
          {children}
        </Box>
      )}
    </Box>
  );

  const renderHorizontalFunnel = () => (
    <Box sx={{ width: '100%' }}>
      {title && (
        <Typography variant="h4" gutterBottom sx={{ mb: 1 }}>
          {title}
        </Typography>
      )}
      {description && (
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          {description}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 2, overflow: 'auto', pb: 2 }}>
        {slots.map((slot, index) => {
          const isCompleted = completedSteps.includes(index);
          const isActive = activeStep === index;
          const isLast = index === slots.length - 1;

          return (
            <React.Fragment key={slot.id}>
              <Card
                onClick={() => handleStepClick(index)}
                sx={{
                  minWidth: 280,
                  cursor: onStepClick ? 'pointer' : 'default',
                  borderTop: `4px solid ${slot.color || theme.palette.primary.main}`,
                  backgroundColor: isActive
                    ? alpha(slot.color || theme.palette.primary.main, 0.05)
                    : 'background.paper',
                  transition: 'all 0.3s ease',
                  '&:hover': onStepClick ? {
                    transform: 'translateY(-2px)',
                    boxShadow: 4,
                  } : {},
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    {slot.icon && (
                      <Box sx={{ color: slot.color || theme.palette.primary.main }}>
                        {slot.icon}
                      </Box>
                    )}
                    <Typography variant="h6" component="div">
                      {slot.title}
                    </Typography>
                    {isCompleted && (
                      <CheckIcon
                        sx={{
                          ml: 'auto',
                          color: theme.palette.success.main,
                        }}
                      />
                    )}
                  </Box>

                  {slot.tags && slot.tags.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 2, flexWrap: 'wrap' }}>
                      {slot.tags.map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.75rem' }}
                        />
                      ))}
                    </Box>
                  )}

                  {slot.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {slot.description}
                    </Typography>
                  )}

                  {slot.content}
                </CardContent>
              </Card>

              {!isLast && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: theme.palette.text.disabled,
                  }}
                >
                  <ArrowForwardIcon />
                </Box>
              )}
            </React.Fragment>
          );
        })}
      </Box>

      {children && (
        <Box sx={{ mt: 4 }}>
          {children}
        </Box>
      )}
    </Box>
  );

  return (
    <Container maxWidth="lg">
      {showProgress && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="caption" color="text.secondary">
            Progress: {completedSteps.length} / {slots.length} completed
          </Typography>
          <Box
            sx={{
              width: '100%',
              height: 4,
              bgcolor: 'grey.200',
              borderRadius: 2,
              mt: 0.5,
            }}
          >
            <Box
              sx={{
                width: `${(completedSteps.length / slots.length) * 100}%`,
                height: '100%',
                bgcolor: theme.palette.success.main,
                borderRadius: 2,
                transition: 'width 0.3s ease',
              }}
            />
          </Box>
        </Box>
      )}

      {orientation === 'vertical' ? renderVerticalFunnel() : renderHorizontalFunnel()}
    </Container>
  );
};

export default FunnelLayout;